/**
 * B2: Message Node Component
 * Channel-agnostic message — AI picks channel at execution time.
 * Shows intent icon and subject/body preview.
 */
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";
import type { MessageNodeData } from "./types";

interface MessageNodeProps {
  node: MessageNodeData;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

export function MessageNode({ node, isSelected, onSelect }: MessageNodeProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      className={cn(
        "flex flex-col gap-1 rounded-lg border-2 px-4 py-3 text-left shadow-sm transition-colors min-w-[200px]",
        "bg-[var(--color-primary)]/5 border-[var(--color-primary)]/20 hover:border-[var(--color-primary)]/70",
        isSelected && "ring-2 ring-[var(--color-ring)] border-[var(--color-primary)]/80",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[var(--color-primary)] uppercase">
          {t("journey.node.message")}
        </span>
        <span className="text-xs text-[var(--color-primary)] bg-[var(--color-primary)]/10 rounded px-1.5 py-0.5">
          {t("journey.channelAi")}
        </span>
      </div>
      {node.subject && (
        <div className="text-sm font-medium text-foreground truncate max-w-[240px]">
          {node.subject}
        </div>
      )}
      {node.body && (
        <div className="text-xs text-muted-foreground truncate max-w-[240px]">
          {node.body.slice(0, 80)}{node.body.length > 80 ? "…" : ""}
        </div>
      )}
      {node.intent && (
        <div className="text-xs text-[var(--color-primary)] mt-1">
          {t("journey.intent")}: {node.intent}
        </div>
      )}
    </button>
  );
}
